// Hälsa: hur båten mår just nu.
//
// b10: vinterläget under Laddning, och kortet Tankar (NMEA 2000) med den senast kända nivån och
// tiden. Dieseln rapporteras bara när strömmen är på, och kortet säger det.
//
// b11: är platsen ej i drift och navet tyst längre än gränsen för tyst nav, säger ett kort högst
// upp att värdena är de senast kända, med tiden, och att de inte bedöms.
//
// b12: korten Motorerna, Kurs och autopilot, och Resor (farder.tsx).
//
// b13: kortet Tankar visar dieseltankarna bredvid varandra, med sidan och litrarna ur tankens
// tabell. Och kortet Vädret vid båten, när båten har en väderstation (vader.tsx).

import { datumOchTid, type Halsa, type Signalvarde, sedan, tal, uteTemp } from "@batkoll/gemensam";
import { Kort, Matare, Rad } from "../komponenter.tsx";
import { AC_KALLA, arGammalt, jaNej, LADDLAGE, position, tal1 } from "../visa.ts";
import { Kurskort, Motorkort, Resekort } from "./farder.tsx";
import { Badtemperaturkort, Givarhalsa, Vindkort } from "./givare.tsx";
import { Vaderkort } from "./vader.tsx";

function Bank(props: { rubrik: string; instans: string; h: Halsa; nu: number }) {
  const s = (namn: string): Signalvarde | undefined => props.h.signaler[`${namn}@${props.instans}`];
  const soc = s("batteri.soc")?.varde;
  const strom = s("batteri.strom")?.varde;
  const procent = typeof soc === "number" ? soc : undefined;
  const riktning =
    typeof strom === "number"
      ? strom > 0.5
        ? "laddar"
        : strom < -0.5
          ? "laddar ur"
          : "vilar"
      : "";
  return (
    <Kort rubrik={props.rubrik} ton={procent !== undefined && procent < 50 ? "varning" : undefined}>
      <div class="stort">
        {procent === undefined ? "–" : `${tal(procent, 0)} %`}
        <span class="liten">{riktning}</span>
      </div>
      <Matare procent={procent} etikett={`${props.rubrik}, laddning`} />
      <Rad namn="Spänning" gammalt={arGammalt(s("batteri.spanning"), props.nu)}>
        {tal1(s("batteri.spanning")?.varde, "V", 2)}
      </Rad>
      <Rad namn="Ström" gammalt={arGammalt(s("batteri.strom"), props.nu)}>
        {tal1(strom, "A")}
      </Rad>
      {/* b16: Victrons batteriövervakare ger också effekten och tiden kvar. */}
      {s("batteri.effekt") && (
        <Rad namn="Effekt" gammalt={arGammalt(s("batteri.effekt"), props.nu)}>
          {tal1(s("batteri.effekt")?.varde, "W", 0)}
        </Rad>
      )}
      {s("batteri.tid_kvar") && (
        <Rad namn="Tid kvar" gammalt={arGammalt(s("batteri.tid_kvar"), props.nu)}>
          {tal1(s("batteri.tid_kvar")?.varde, "h", 1)}
        </Rad>
      )}
    </Kort>
  );
}

const TANKNAMN: Record<string, string> = {
  diesel: "Diesel",
  farskvatten: "Färskvatten",
  gravatten: "Gråvatten",
  svartvatten: "Svartvatten",
};
const TANKORDNING = Object.keys(TANKNAMN);

/** Tankens namn ur instansen: "diesel" blir Diesel, och "diesel2" Diesel 2. */
function tanknamn(instans: string): { typ: string; namn: string } {
  const m = /^([a-z]+?)(\d*)$/.exec(instans);
  const typ = m?.[1] ?? instans;
  return { typ, namn: `${TANKNAMN[typ] ?? typ}${m?.[2] ? ` ${m[2]}` : ""}` };
}

/**
 * b10: tankarna, med den senast kända nivån och tiden. b13: dieseltankarna bredvid varandra, med
 * litrarna ur tankens tabell (eller linjärt), och sidan de hör till.
 */
function Tankar(props: { h: Halsa; nu: number }) {
  const diesel = (props.h.dieseltankar ?? []).filter((t) => t.niva !== null);
  const harDiesel = diesel.length > 0;
  const tankar = Object.entries(props.h.signaler)
    .filter(([k, sv]) => k.startsWith("tank.niva@") && typeof sv.varde === "number")
    .map(([k, sv]) => {
      const instans = k.slice("tank.niva@".length);
      return { instans, ...tanknamn(instans), niva: sv.varde as number, tid: sv.tid };
    })
    .filter((t) => !(harDiesel && t.typ === "diesel"))
    .sort(
      (a, b) =>
        TANKORDNING.indexOf(a.typ) - TANKORDNING.indexOf(b.typ) ||
        a.instans.localeCompare(b.instans),
    );
  if (tankar.length === 0 && !harDiesel) return null;
  const svart = Math.max(-1, ...tankar.filter((t) => t.typ === "svartvatten").map((t) => t.niva));
  const ton = svart > 90 ? "akut" : svart > 75 ? "varning" : undefined;
  return (
    <Kort rubrik="Tankar" ton={ton}>
      {harDiesel && (
        <div class="dieseltankar">
          {diesel.map((t) => (
            <section key={t.instans} class="dieseltank" aria-label={t.namn}>
              <h3>{t.namn}</h3>
              <div class="stort">{tal(t.niva ?? 0, 0)} %</div>
              <p>
                {t.liter !== null
                  ? `${tal(t.liter, 0)} liter${t.hur === "tabell" ? " (tankens tabell)" : " (linjärt)"}`
                  : "liter: ställ in volymen"}
              </p>
              <p class="liten">senast kända{t.tid !== null ? `, ${sedan(t.tid, props.nu)}` : ""}</p>
            </section>
          ))}
        </div>
      )}
      {tankar.map((t) => {
        const volym = props.h.signaler[`tank.volym@${t.instans}`]?.varde;
        const nar =
          t.typ === "diesel" ? `senast kända, ${sedan(t.tid, props.nu)}` : sedan(t.tid, props.nu);
        return (
          <Rad key={t.instans} namn={t.namn}>
            {tal(t.niva, 0)} %{typeof volym === "number" ? `, ${tal(volym, 0)} l` : ""} ({nar})
          </Rad>
        );
      })}
      {(harDiesel || tankar.some((t) => t.typ === "diesel")) && (
        <p class="liten">
          Dieseln rapporteras bara när strömmen är på. Värdet är det senast kända.
        </p>
      )}
    </Kort>
  );
}

export function Halsovy(props: { h: Halsa; nu: number }) {
  const { h, nu } = props;
  const s = (nyckel: string) => h.signaler[nyckel];
  const v = (nyckel: string) => s(nyckel)?.varde;
  const g = (nyckel: string) => arGammalt(s(nyckel), nu);
  // b20-1: temperaturen ute ur uteTemp, som på kortet och i husets vy.
  const ute = uteTemp(h.signaler, nu);
  const landstrom = v("landstrom.finns");
  const mv = h.tolkning.mastervolt;
  const gen = h.tolkning.generator;

  return (
    <div class="rutnat">
      {h.senastKanda != null && (
        <Kort rubrik="Senast kända värden" bred>
          <p class="meddelande">
            Platsen är ej i drift, och navet hördes senast av {sedan(h.senastKanda, nu)} (
            {datumOchTid(h.senastKanda)}). Värdena nedan är de senast kända, och bedöms inte.
          </p>
        </Kort>
      )}
      {mv.kontakt === false && (
        <Kort rubrik="Mastervolt" ton="varning" bred>
          <p class="meddelande">
            Kontakten med Mastervolt är bruten. Värdena nedan kan vara gamla.
          </p>
        </Kort>
      )}
      {mv.ramfel && (
        <Kort rubrik="Fel i navet" ton="akut" bred>
          <p class="meddelande">
            Mastervolts gränssnitt svarar att navets fråga är fel: {mv.ramfel}. Det är ett fel i
            navets kod, inte ombord. Se navets logg.
          </p>
        </Kort>
      )}
      {mv.saknas.length > 0 && (
        <Kort rubrik="Mastervolt" bred>
          <p class="meddelande">Svarar inte: {mv.saknas.join(", ")}</p>
        </Kort>
      )}

      <Bank rubrik="Förbrukningsbanken" instans="forbrukning" h={h} nu={nu} />
      <Bank rubrik="Navigationsbanken" instans="navigation" h={h} nu={nu} />

      <Kort rubrik="Landström" ton={landstrom === false ? "varning" : undefined}>
        <div class="stort">
          {landstrom === true ? "Ansluten" : landstrom === false ? "Borta" : "–"}
        </div>
        <Rad namn="Spänning" gammalt={g("landstrom.spanning")}>
          {tal1(v("landstrom.spanning"), "V", 0)}
        </Rad>
        <Rad namn="Ström" gammalt={g("landstrom.strom")}>
          {tal1(v("landstrom.strom"), "A")}
        </Rad>
        <Rad namn="Effekt" gammalt={g("landstrom.effekt")}>
          {tal1(v("landstrom.effekt"), "W", 0)}
        </Rad>
      </Kort>

      <Kort rubrik="Laddning">
        {h.vinterlage && <Rad namn="Vinterläge">{h.vinterlage}</Rad>}
        <Rad namn="Masscombi">{LADDLAGE[String(v("laddare.lage@masscombi"))] ?? "–"}</Rad>
        <Rad namn="230 V från">{AC_KALLA[String(v("ac.kalla"))] ?? "–"}</Rad>
        <Rad namn="Växelriktaren">{jaNej(v("vaxelriktare.pa"), "På", "Av")}</Rad>
        <Rad namn="Startbatteriernas laddare">
          {LADDLAGE[String(v("laddare.lage@cm1225"))] ?? "–"}
        </Rad>
        <Rad namn="Till navigationsbanken">
          {LADDLAGE[String(v("laddare.lage@plus1250"))] ?? "–"}
        </Rad>
        {/* b16: Victrons laddare, när de finns. */}
        {s("laddare.lage@vebus") && (
          <Rad namn="MultiPlus eller Quattro">
            {LADDLAGE[String(v("laddare.lage@vebus"))] ?? "–"}
          </Rad>
        )}
        {s("laddare.lage@dcdc") && (
          <Rad namn="Orion DC/DC">{LADDLAGE[String(v("laddare.lage@dcdc"))] ?? "–"}</Rad>
        )}
        {s("laddare.lage@smartladdare") && (
          <Rad namn="Blue Smart">{LADDLAGE[String(v("laddare.lage@smartladdare"))] ?? "–"}</Rad>
        )}
      </Kort>

      {/* b16: solcellerna, Victrons solladdare via GX eller Bluetooth. */}
      {(s("sol.effekt") || s("sol.skord")) && (
        <Kort rubrik="Solcellerna">
          <div class="stort">{tal1(v("sol.effekt"), "W", 0)}</div>
          <Rad namn="Skörden i dag" gammalt={g("sol.skord")}>
            {tal1(v("sol.skord"), "kWh", 2)}
          </Rad>
          <Rad namn="Läget">{LADDLAGE[String(v("sol.lage"))] ?? "–"}</Rad>
        </Kort>
      )}

      <Kort rubrik="Generatorn" ton={gen.lage === "fel" ? "varning" : undefined}>
        <div class="stort mellan">{gen.text}</div>
      </Kort>

      <Kort rubrik="Startbatterierna">
        <Rad namn="Styrbord" gammalt={g("batteri.spanning@start_sb")}>
          {tal1(v("batteri.spanning@start_sb"), "V", 2)}
        </Rad>
        <Rad namn="Babord" gammalt={g("batteri.spanning@start_bb")}>
          {tal1(v("batteri.spanning@start_bb"), "V", 2)}
        </Rad>
      </Kort>

      <Kort rubrik="Temperaturer">
        <Rad namn="Kabinen" gammalt={g("kabin.temp")}>
          {tal1(v("kabin.temp"), "°C")}
        </Rad>
        <Rad namn="Fukt i kabinen" gammalt={g("kabin.fukt")}>
          {tal1(v("kabin.fukt"), "%", 0)}
        </Rad>
        <Rad namn="Motorrummet" gammalt={g("motorrum.temp")}>
          {tal1(v("motorrum.temp"), "°C")}
        </Rad>
        {s("kyl.temp") && (
          <Rad namn="Kylskåpet" gammalt={g("kyl.temp")}>
            {tal1(v("kyl.temp"), "°C")}
          </Rad>
        )}
        {s("frys.temp") && (
          <Rad namn="Frysen" gammalt={g("frys.temp")}>
            {tal1(v("frys.temp"), "°C")}
          </Rad>
        )}
        <Rad namn="Fukt i motorrummet" gammalt={g("motorrum.fukt")}>
          {tal1(v("motorrum.fukt"), "%", 0)}
        </Rad>
        <Rad
          namn="Ute"
          gammalt={ute ? ute.gammalt || arGammalt({ varde: ute.varde, tid: ute.tid }, nu) : false}
        >
          {tal1(ute?.varde, "°C")}
          {ute?.text && <small class="ute-kalla">{ute.text}</small>}
        </Rad>
        <Rad namn="Fukt ute" gammalt={g("ute.fukt")}>
          {tal1(v("ute.fukt"), "%", 0)}
        </Rad>
        {(s("batterirum.temp") || s("batterirum.fukt")) && (
          <>
            <Rad namn="Batteriutrymmet" gammalt={g("batterirum.temp")}>
              {tal1(v("batterirum.temp"), "°C")}
            </Rad>
            <Rad namn="Fukt i batteriutrymmet" gammalt={g("batterirum.fukt")}>
              {tal1(v("batterirum.fukt"), "%", 0)}
            </Rad>
          </>
        )}
        <Rad namn="Förbrukningsbanken" gammalt={g("batteri.temp@forbrukning")}>
          {tal1(v("batteri.temp@forbrukning"), "°C")}
        </Rad>
        <Rad namn="Navigationsbanken" gammalt={g("batteri.temp@navigation")}>
          {tal1(v("batteri.temp@navigation"), "°C")}
        </Rad>
      </Kort>

      <Kort rubrik="Position">
        <div class="position">{position(v("position"))}</div>
        <Rad namn="Fart" gammalt={g("instrument.fart_kn")}>
          {tal1(v("instrument.fart_kn"), "kn")}
        </Rad>
        <Rad namn="Djup" gammalt={g("instrument.djup_m")}>
          {tal1(v("instrument.djup_m"), "m")}
        </Rad>
        <Rad namn="Vind, båtens givare" gammalt={g("instrument.vind_ms")}>
          {tal1(v("instrument.vind_ms"), "m/s")}
        </Rad>
      </Kort>

      <Tankar h={h} nu={nu} />

      {h.farder && <Resekort f={h.farder} platsId={h.plats.id} nu={nu} />}
      <Motorkort h={h} nu={nu} />
      <Kurskort h={h} nu={nu} />

      {(typeof v("masterbus.lage") === "string" || typeof v("nmea2000.lage") === "string") && (
        <Kort
          rubrik="Bussarna"
          ton={
            /fel på bussen|stoppade/i.test(String(v("masterbus.lage") ?? ""))
              ? "varning"
              : undefined
          }
        >
          {typeof v("masterbus.lage") === "string" && (
            <Rad namn="MasterBus" gammalt={g("masterbus.lage")}>
              {String(v("masterbus.lage"))}
            </Rad>
          )}
          {typeof v("nmea2000.lage") === "string" && (
            <Rad namn="NMEA 2000" gammalt={g("nmea2000.lage")}>
              {String(v("nmea2000.lage"))}
            </Rad>
          )}
        </Kort>
      )}

      {h.vaderstation && <Vaderkort v={h.vaderstation} bat tidszon={h.plats.tidszon} nu={nu} />}
      {h.vind && <Vindkort v={h.vind} tidszon={h.plats.tidszon} nu={nu} />}
      {h.badtemperatur && (
        <Badtemperaturkort b={h.badtemperatur} tidszon={h.plats.tidszon} nu={nu} />
      )}
      <Givarhalsa h={h} nu={nu} />

      <Kort rubrik="Vattnet">
        <div class="stort mellan">
          {typeof v("vatten.temp") === "number"
            ? `${tal(v("vatten.temp") as number, 1)} °C vid båten`
            : "–"}
        </div>
        {s("vatten.temp") && <p class="liten">Mätt {sedan(s("vatten.temp")?.tid ?? nu, nu)}</p>}
      </Kort>
    </div>
  );
}
